import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import * as TE from 'fp-ts/lib/TaskEither';

import CardHtmlConverter from './converter/CardHtmlConverter';
import DeckCardsConverter from './converter/DeckCardsConverter';
import DeckConverter from './converter/DeckConverter';
import NextStepConverter from './converter/NextStepConverter';
import ResourcesConverter from './converter/ResourcesConverter';
import StatConverter from './converter/StatConverter';
import RestService from './RestService';
import { cardHtmlTOResponseDecoder } from './to/CardHtmlTO';
import {
  deckCardsTOResponseDecoder,
  decksTOResponseDecoder,
  resourcesItemsTOResponseDecoder,
} from './to/DeckTO';
import { nextStepTOResponseDecoder } from './to/NextStepTO';
import { StatTO } from './to/StatTO';

import { CardHtml } from '../model/domain/CardHtml';
import { DeckCard, Deck, ResourcesItem } from '../model/domain/Deck';
import { NextStep } from '../model/domain/NextStep';
import { Stat } from '../model/domain/Stat';
import { BSTask } from '../model/types';

const nextStep = (stat: O.Option<Stat>, skipSurvey?: string): BSTask<NextStep> => {
  const url = `/app/study${skipSurvey === 'skipSurvey' ? '?skipSurvey=true' : ''}`;
  const body: undefined | StatTO = pipe(
    stat,
    O.map<Stat, StatTO>((s: Stat) => StatConverter.fromDomain(s)),
    O.getOrElse<undefined | StatTO>(() => undefined)
  );

  return pipe(
    RestService.put(url, body),
    nextStepTOResponseDecoder,
    TE.map(NextStepConverter.toDomain)
  );
};

const getUserLibraryDecks = (): BSTask<Deck[]> => {
  const url = '/app/library/decks?deckType=0';

  return pipe(RestService.get(url), decksTOResponseDecoder, TE.map(DeckConverter.toDomain));
};

const getUserLibraryDeckCards = (deckId: string): BSTask<DeckCard[]> => {
  const url = `/app/library/decks/${deckId}`;

  return pipe(
    RestService.get(url),
    deckCardsTOResponseDecoder,
    TE.map(DeckCardsConverter.toDomain)
  );
};

const getUserLibraryCard = (cardId: string): BSTask<CardHtml> => {
  const url = `/app/library/cards/${cardId}`;

  return pipe(RestService.get(url), cardHtmlTOResponseDecoder, TE.map(CardHtmlConverter.toDomain));
};

const getUserLibraryResources = (): BSTask<ResourcesItem[]> => {
  const url = '/app/library/extra-resources';

  return pipe(
    RestService.get(url),
    resourcesItemsTOResponseDecoder,
    TE.map(ResourcesConverter.toDomain)
  );
};

export default {
  nextStep: nextStep,
  getUserLibraryDecks,
  getUserLibraryDeckCards,
  getUserLibraryCard,
  getUserLibraryResources,
};
